import { Component, computed, effect, inject, input, OnInit } from '@angular/core';
import { ILpCanvasComponent, ILpCanvasElementContext } from '@gametailors/leerplus';
import { LpRidLocationLabelCanvasComponent, LpRidLocationStop } from '../../../../typings/schema';
import { RidMapLabel, RidMapLabelState } from '../../components/rid-map-label/rid-map-label';
import { RidProgress } from '../../components/rid-progress-dot/rid-progress-dot';
import { LEERPLUS } from '../../rid-game.utils';
import { buildLocationMap } from '../../rid-location-map.utils';
import { RidHomeworkService } from '../../services/rid-homework.service';
import { RidJourneyService } from '../../services/rid-journey.service';

/** The name of a location on its province's map, with a dot per stop showing how far its homework is. While the player chooses a location, the ones to choose from only show a question mark, so the name comes as the reward of choosing it. A click chooses the location while it's one to choose from, and opens its map otherwise (built from the project's location map template); a locked location only shows its lock, and a click on it does nothing. */
@Component({
  selector: 'rid-location-label-canvas-component',
  imports: [RidMapLabel],
  templateUrl: './rid-location-label-canvas-component.html',
  host: {
    class: 'grid size-full place-items-center',
  },
})
export class RidLocationLabelCanvasComponent implements OnInit, ILpCanvasComponent<LpRidLocationLabelCanvasComponent> {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridHomeworkService = inject(RidHomeworkService);
  private readonly $ridJourneyService = inject(RidJourneyService);

  comp = input.required<LpRidLocationLabelCanvasComponent>();
  context = input.required<ILpCanvasElementContext>();

  protected readonly locked = computed(() => this.$ridJourneyService.isLocationLocked(this.comp().location));
  /** While choosing, the ones to choose from keep their name a mystery until they're chosen; the ones started before keep theirs. */
  protected readonly labelState = computed<RidMapLabelState>(() => {
    const location = this.comp().location;
    if (this.locked()) return 'LOCKED';

    if (this.$ridJourneyService.isChoosingLocation() && !this.$ridJourneyService.isLocationStarted(location)) return 'MYSTERY';

    return this.$ridJourneyService.isLocationExplored(location) ? 'FINISHED' : 'OPEN';
  });
  /** There's homework left there for today, so the player is called there. */
  protected readonly hasWorkToday = computed(() => this.$ridJourneyService.hasLocationWorkToday(this.comp().location));
  protected readonly stopDots = computed(() =>
    this.$ridHomeworkService.sortStopsByStartOrder(this.comp().location.stops).map((stop) => this.getStopProgress(stop)),
  );

  constructor() {
    // the same hover highlight Leerplus gives the elements it makes clickable (OnClickCanvasComponent), but only while it can be clicked
    effect(() => {
      if (this.locked()) {
        this.context().requirePointerEvents('default').clearHoverStyle();
        return;
      }

      this.context().requirePointerEvents('pointer').setHoverStyle({ filter: 'brightness(1.2)' });
    });
  }

  ngOnInit(): void {
    this.context()
      .transition('filter 0.25s ease')
      .onClick(() => this.onClick());
  }

  private async onClick() {
    const location = this.comp().location;
    if (this.locked() || this.$ridJourneyService.chooseLocation(location)) return;

    const settings = this.$lpApiService.getProjectSettings().code;
    await this.$lpApiService.switchMap(buildLocationMap(settings.locationMapTemplate, location), settings.defaultMapTransition);
  }

  private getStopProgress(stop: LpRidLocationStop): RidProgress {
    if (this.$ridHomeworkService.isStopFinished(stop)) return 'FINISHED';

    if (this.$ridHomeworkService.isStopInProgress(stop)) return 'IN_PROGRESS';

    return 'NOT_STARTED';
  }
}
